/**
 * Tenant : projection UI d'un tenant backend. Le shape settings
 * correspond à `internal/domain/tenant.Settings`.
 */
export interface Tenant {
    readonly id: string
    readonly name: string
    readonly createdAt: string
    readonly settings: TenantSettings
}

export interface TenantSettings {
    readonly sanitizationMode: string
    readonly classifierEnabled: boolean
    readonly auditContentMode: string
    /** Chantier 2-A — cache de réponse exact-match (opt-in). */
    readonly responseCacheEnabled: boolean
    /** TTL d'une entrée de cache (secondes). 0 = défaut système (5 min). */
    readonly responseCacheTtlSeconds: number
}

export interface CreateTenantInput {
    readonly id: string
    readonly name: string
    readonly settings?: TenantSettings
}

export interface UpdateTenantInput {
    readonly name?: string
    readonly settings?: TenantSettings
}

export type TenantErrorCode =
    | 'tenant_not_found'
    | 'tenant_exists'
    | 'invalid_id'
    | 'invalid_name'
    | 'invalid_settings'
    | 'tenant_scope'
    | 'csrf_required'
    | 'csrf_invalid'
    | 'unauthorized'
    | 'forbidden'
    | 'internal_error'
    | 'network_error'
    | 'unknown'

export class TenantError extends Error {
    readonly code: TenantErrorCode
    readonly httpStatus: number | null
    constructor(code: TenantErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'TenantError'
    }
}

export function isTenantError(err: unknown): err is TenantError {
    return err instanceof TenantError
}

export const KNOWN_TENANT_CODES: readonly TenantErrorCode[] = [
    'tenant_not_found',
    'tenant_exists',
    'invalid_id',
    'invalid_name',
    'invalid_settings',
    'tenant_scope',
    'csrf_required',
    'csrf_invalid',
    'unauthorized',
    'forbidden',
    'internal_error',
    'network_error',
    'unknown',
] as const

/**
 * SANITIZATION_MODES + AUDIT_CONTENT_MODES : valeurs admissibles pour
 * les selects du form admin. Alignés avec le backend
 * (`internal/domain/tenant.Settings`).
 */
export const SANITIZATION_MODES = ['off', 'tokenize', 'redact'] as const
export const AUDIT_CONTENT_MODES = ['zero', 'tokenized', 'full'] as const

export const DEFAULT_TENANT_SETTINGS: TenantSettings = {
    sanitizationMode: 'tokenize',
    classifierEnabled: true,
    auditContentMode: 'tokenized',
    responseCacheEnabled: false,
    responseCacheTtlSeconds: 0,
}
